/* eslint-disable @angular-eslint/no-output-on-prefix */
/* eslint-disable max-classes-per-file */
import { CommonModule, TitleCasePipe } from '@angular/common';
import {
  Component,
  Input,
  NgModule,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { BehaviorSubject, Subject, fromEvent } from 'rxjs';
import { auditTime, map, takeUntil } from 'rxjs/operators';
import { Profile } from '../core/profile/profile.service';
import { LocalePickerModule } from '../locale-picker/locale-picker.component';
import { Link } from '../model/link.model';
import { MIN_DESKTOP_WIDTH } from '../core/constants';
import { ConfigService } from '../core';
import { OrganizationInterface } from '../../interfaces/organization.interface';
import { OrganizationService } from '../core/organization/organization.service';

@Component({
    selector: 'lib-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class HeaderComponent implements OnDestroy, OnChanges {
  @Input() links: Link[] = [];
  @Input() brandLogo = '/assets/img/synlab-logo.svg';
  @Input() myAccountLink: string;
  @Input() brandLogoLink: string;
  @Input() disableLinks = false;
  @Input() user?: Profile;
  @Input() organizations: OrganizationInterface[] = [];

  @Output() onLogout = new EventEmitter();
  @Output() onMyAccount = new EventEmitter();

  isMobile$ = new BehaviorSubject<boolean>(
    document.documentElement.clientWidth < MIN_DESKTOP_WIDTH,
  );
  isMobileMenuOpen$ = new BehaviorSubject<boolean>(false);
  destroy$: Subject<boolean> = new Subject<boolean>();

  isUkDfp$ = this.config.config$.pipe(map(config => config.WhiteLabelName === 'uk-dfp'));
  isGlobal$ = this.config.config$.pipe(map(config => config.WhiteLabelName === 'global'));

  public isSavingOrg: boolean;

  public selectedOrg: OrganizationInterface = null;

  constructor(
    private readonly router: Router,
    private readonly config: ConfigService,
    private readonly organizationService: OrganizationService,
  ) {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.isMobileMenuOpen$.next(false));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.user) {
      const current = changes?.user?.currentValue;
      if (current?.synlabId) {
        this.fetchSessionOrg();
      }
    }
  }

  private async fetchSessionOrg() {
    const organization = await this.organizationService.getUserSessionOrg();
    this.selectedOrg = organization;
  }

  resize$ = fromEvent(window, 'resize')
    .pipe(takeUntil(this.destroy$), auditTime(200))
    .subscribe(() => {
      this.isMobile$.next(document.documentElement.clientWidth < MIN_DESKTOP_WIDTH);
    });

  toggleMenu() {
    this.isMobileMenuOpen$.next(!this.isMobileMenuOpen$.getValue());
  }

  logout() {
    this.onLogout.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public get getDisplayedLinks(): Link[] {
    return this.links.filter(v => v?.url);
  }

  public async changeOrganization(organization: OrganizationInterface) {
    if (this.isSavingOrg) return;

    try {
      this.isSavingOrg = true;
      await this.organizationService.doSaveOrgId(organization.organizationId);
    } finally {
      this.isSavingOrg = false;
    }
  }
}

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    LocalePickerModule,
    TitleCasePipe,
  ],
  declarations: [HeaderComponent],
  exports: [HeaderComponent],
})
export class HeaderModule {}
